import React, { useEffect, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

const FEATURES = [
  'Adaptive plan engine (Cut · Maintain · Bulk · Transform)',
  'Injury- and equipment-aware daily call',
  'AI meal plans built to your daily macros',
  'Food snap — photo to calories & macros',
  'Progressive overload tracker',
  'Recovery & physio sessions',
  'AI Coach with full context of your plan',
  'Weekly check-ins and progress trend',
];

const PriceCard = ({ badge, label, price, strikePrice, suffix, sub, highlighted, testId }) => (
  <div
    data-testid={testId}
    className={`relative rounded-2xl border p-6 lg:p-7 flex flex-col ${
      highlighted
        ? 'border-[#BEFF38] bg-gradient-to-br from-[#BEFF38]/[0.08] to-transparent shadow-[0_0_40px_-10px_rgba(190,255,56,0.35)]'
        : 'border-zinc-800 bg-zinc-950/60'
    }`}
  >
    {badge && (
      <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#BEFF38] text-black text-[10px] font-black uppercase tracking-widest">
        {badge}
      </span>
    )}
    <p className="text-zinc-300 font-semibold text-sm">{label}</p>
    <div className="mt-3 flex items-baseline gap-2">
      <span className="text-white font-black text-5xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{price}</span>
      {strikePrice && (
        <span className="text-zinc-500 text-lg font-semibold line-through" data-testid={`${testId}-strike`}>
          {strikePrice}
        </span>
      )}
      <span className="text-zinc-400 text-base font-semibold">{suffix}</span>
    </div>
    <p className="text-zinc-500 text-sm mt-1">{sub}</p>
  </div>
);

const Pricing = () => {
  const [eb, setEb] = useState(null); // earlybird status

  useEffect(() => {
    let alive = true;
    api.earlybirdStatus()
      .then((s) => { if (alive) setEb(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const showFounder = eb?.available;
  const spotsLeft = eb?.spots_left ?? null;

  return (
    <section className="bg-[#0b0b0c] py-14 lg:py-20" id="pricing" data-testid="pricing-section">
      <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
        <h2 className="text-white text-3xl lg:text-4xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Built for your phone. Priced to stick with.
        </h2>
        <p className="mt-3 text-zinc-400 max-w-2xl text-base leading-relaxed">
          The app gives you the full adaptive experience — your plan, daily call, meal plans to budget, food snap, overload tracker and AI coach in your pocket.
        </p>

        {showFounder && (
          <div
            data-testid="founder-banner"
            className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#BEFF38]/15 border border-[#BEFF38]/40 text-[#BEFF38]"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-wider font-bold">
              Founder offer · £19/year forever · first 100 · {spotsLeft} spots left
            </span>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          <PriceCard
            testId="price-card-monthly"
            label="Monthly"
            price="£5"
            suffix="/ month"
            sub="Cancel any time."
          />
          <PriceCard
            testId="price-card-three-month"
            label="3 months"
            price="£12"
            suffix="/ 3 months"
            sub="≈ £4.00 / month."
          />
          <PriceCard
            testId="price-card-six-month"
            label="6 months"
            price="£18"
            suffix="/ 6 months"
            sub="≈ £3.00 / month — 40% off."
          />
          {showFounder ? (
            <PriceCard
              testId="price-card-annual"
              badge={`Founder · ${spotsLeft} left`}
              label="Annual — Founder"
              price="£19"
              strikePrice="£25"
              suffix="/ year"
              sub="Founder rate — locked forever (may adjust with UK inflation). First 100 only."
              highlighted
            />
          ) : (
            <PriceCard
              testId="price-card-annual"
              badge="Best value"
              label="Annual"
              price="£25"
              suffix="/ year"
              sub="≈ £2.08 / month — 58% off monthly."
              highlighted
            />
          )}
        </div>

        <p className="mt-3 text-[11px] text-zinc-500 text-center leading-relaxed max-w-2xl mx-auto" data-testid="pricing-inflation-note">
          Prices may be adjusted from time to time in line with UK inflation — including the founder rate. Any change is announced in the app 30 days before it applies, and existing subscriptions run out at the price you already paid.
        </p>

        <div className="mt-8">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6" data-testid="features-card">
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#BEFF38]">Everything included</p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex gap-2 text-zinc-300 text-sm leading-snug">
                  <Check className="w-4 h-4 text-[#BEFF38] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
